import { useState, useRef, useCallback } from "react";
import { type UserStory } from "@/lib/projectStore";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { GripVertical, Flame, CheckCircle2, Timer, Archive } from "lucide-react";

const COLUMNS: { id: UserStory["status"]; label: string; icon: React.ComponentType<any>; emptyText: string }[] = [
  { id: "backlog", label: "Backlog", icon: Archive, emptyText: "Stories waiting to be picked up" },
  { id: "in-progress", label: "In Progress", icon: Timer, emptyText: "Work actively being done" },
  { id: "done", label: "Done", icon: CheckCircle2, emptyText: "Completed work this sprint" },
];

const priorityConfig: Record<string, { color: string; accent: string; label: string; icon: string }> = {
  must: { color: "border-l-destructive bg-destructive/[0.03]", accent: "text-destructive", label: "Must", icon: "🔴" },
  should: { color: "border-l-accent bg-accent/[0.03]", accent: "text-accent", label: "Should", icon: "🟠" },
  could: { color: "border-l-muted-foreground/30 bg-secondary/30", accent: "text-muted-foreground", label: "Could", icon: "🔵" },
  wont: { color: "border-l-muted bg-muted/20", accent: "text-muted-foreground/60", label: "Won't", icon: "⚪" },
};

interface Props {
  stories: UserStory[];
  onChange: (stories: UserStory[]) => void;
}

export default function KanbanBoard({ stories, onChange }: Props) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [view, setView] = useState<"board" | "compact">("board");
  const dragRef = useRef<{ id: string; sourceCol: string } | null>(null);

  const grouped: Record<string, UserStory[]> = { backlog: [], "in-progress": [], done: [] };
  stories.forEach((s) => {
    const col = s.status || "backlog";
    if (grouped[col]) grouped[col].push(s);
    else grouped["backlog"].push(s);
  });

  const handleDragStart = (e: React.DragEvent, story: UserStory) => {
    setDragId(story.id);
    dragRef.current = { id: story.id, sourceCol: story.status };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", story.id);
  };

  const handleDragOver = (e: React.DragEvent, colId: string, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCol(colId);
    setDragOverIdx(idx);
  };

  const handleDragOverColumn = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCol(colId);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent, targetCol: string, targetIdx?: number) => {
      e.preventDefault();
      const storyId = e.dataTransfer.getData("text/plain") || dragId;
      if (!storyId) return;

      const newStatus = targetCol as UserStory["status"];
      const updated = stories.map((s) =>
        s.id === storyId ? { ...s, status: newStatus } : s
      );

      if (targetIdx !== undefined) {
        const colStories = updated.filter((s) => s.status === newStatus && s.id !== storyId);
        const movedStory = updated.find((s) => s.id === storyId);
        if (movedStory) {
          colStories.splice(targetIdx, 0, movedStory);
          const otherStories = updated.filter((s) => s.status !== newStatus);
          onChange([...otherStories, ...colStories]);
        } else {
          onChange(updated);
        }
      } else {
        onChange(updated);
      }

      setDragId(null);
      setDragOverCol(null);
      setDragOverIdx(null);
      dragRef.current = null;
    },
    [stories, onChange, dragId]
  );

  const handleDragEnd = () => {
    setDragId(null);
    setDragOverCol(null);
    setDragOverIdx(null);
    dragRef.current = null;
  };

  const totalPoints = (items: UserStory[]) =>
    items.reduce((a, s) => a + (s.storyPoints || 0), 0);

  const completedPts = totalPoints(grouped["done"]);
  const totalPts = totalPoints(stories);
  const velocityPct = totalPts > 0 ? Math.round((completedPts / totalPts) * 100) : 0;

  return (
    <div className="p-4 space-y-4">
      {/* Header with velocity meter */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
            Sprint Board
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Drag stories between columns · {stories.length} items
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Velocity indicator */}
          {totalPts > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <Flame className={cn("h-3.5 w-3.5", velocityPct > 50 ? "text-accent" : "text-muted-foreground")} />
                <span className="text-xs font-mono font-bold text-foreground">{completedPts}/{totalPts} pts</span>
              </div>
              <div className="w-20 h-1.5 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-accent transition-all duration-500"
                  style={{ width: `${velocityPct}%` }}
                />
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">{velocityPct}%</span>
            </div>
          )}
          {/* View toggle */}
          <div className="flex border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setView("board")}
              className={cn(
                "px-2.5 py-1 text-[10px] font-mono transition-colors",
                view === "board" ? "bg-accent text-accent-foreground font-bold" : "text-muted-foreground hover:bg-secondary"
              )}
            >
              Cards
            </button>
            <button
              onClick={() => setView("compact")}
              className={cn(
                "px-2.5 py-1 text-[10px] font-mono transition-colors",
                view === "compact" ? "bg-accent text-accent-foreground font-bold" : "text-muted-foreground hover:bg-secondary"
              )}
            >
              Compact
            </button>
          </div>
        </div>
      </div>

      {stories.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          Add user stories in the "User Stories" tab first.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-3 min-h-[450px] min-w-[640px]">
          {COLUMNS.map((col) => {
            const items = grouped[col.id] || [];
            const isOver = dragOverCol === col.id;
            const ColIcon = col.icon;

            return (
              <div
                key={col.id}
                className={cn(
                  "flex-1 min-w-[240px] rounded-lg flex flex-col transition-all duration-200",
                  isOver
                    ? "ring-2 ring-accent/40 bg-accent/[0.03]"
                    : "bg-secondary/20"
                )}
                onDragOver={(e) => handleDragOverColumn(e, col.id)}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                {/* Column header — editorial style */}
                <div className="px-4 py-3 border-b border-border/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ColIcon className={cn("h-3.5 w-3.5", col.id === "done" ? "text-accent" : "text-muted-foreground")} />
                      <span className="text-xs font-display font-bold text-foreground tracking-wide">
                        {col.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-mono text-muted-foreground bg-secondary/80 px-1.5 py-0.5 rounded">
                        {items.length}
                      </span>
                      {totalPoints(items) > 0 && (
                        <span className="text-[10px] font-mono text-accent bg-accent/10 px-1.5 py-0.5 rounded">
                          {totalPoints(items)}p
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Column progress bar */}
                  {col.id !== "backlog" && totalPts > 0 && (
                    <div className="mt-2 h-0.5 bg-border/30 rounded-full overflow-hidden">
                      <div
                        className={cn("h-full rounded-full transition-all", col.id === "done" ? "bg-accent" : "bg-primary/40")}
                        style={{ width: `${(totalPoints(items) / totalPts) * 100}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Cards */}
                <div className="flex-1 p-2 space-y-1.5 overflow-auto">
                  {items.map((story, idx) => {
                    const pc = priorityConfig[story.priority] || priorityConfig.could;
                    return (
                      <div
                        key={story.id}
                        onDragOver={(e) => handleDragOver(e, col.id, idx)}
                        onDrop={(e) => {
                          e.stopPropagation();
                          handleDrop(e, col.id, idx);
                        }}
                      >
                        {dragOverCol === col.id && dragOverIdx === idx && dragId !== story.id && (
                          <div className="h-0.5 bg-accent rounded-full mb-1 animate-fade-in" />
                        )}

                        {view === "compact" ? (
                          /* Compact row view */
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, story)}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              "flex items-center gap-2 px-2.5 py-1.5 rounded border-l-2 cursor-grab active:cursor-grabbing transition-all text-xs",
                              pc.color,
                              dragId === story.id && "opacity-30 scale-95",
                              col.id === "done" && "opacity-70",
                              "hover:shadow-sm"
                            )}
                          >
                            <span className="text-[10px]">{pc.icon}</span>
                            <span className={cn("flex-1 font-body truncate", col.id === "done" && "line-through text-muted-foreground")}>
                              {story.title}
                            </span>
                            {story.storyPoints && (
                              <span className="text-[9px] font-mono text-muted-foreground shrink-0">
                                {story.storyPoints}p
                              </span>
                            )}
                          </div>
                        ) : (
                          /* Full card view */
                          <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, story)}
                            onDragEnd={handleDragEnd}
                            className={cn(
                              "rounded-md p-3 cursor-grab active:cursor-grabbing transition-all border-l-[3px]",
                              pc.color,
                              "border border-border/40",
                              dragId === story.id && "opacity-30 scale-95",
                              col.id === "done" && "opacity-75",
                              "hover:shadow-md hover:translate-y-[-1px]"
                            )}
                          >
                            <div className="flex items-start gap-2">
                              <GripVertical className="h-3.5 w-3.5 text-muted-foreground/20 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className={cn(
                                  "text-xs font-semibold text-card-foreground font-body leading-snug",
                                  col.id === "done" && "line-through text-muted-foreground"
                                )}>
                                  {story.title}
                                </p>
                                {story.asA && (
                                  <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2 italic">
                                    As a {story.asA}, I want {story.iWant}
                                  </p>
                                )}
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                  <span className={cn("text-[9px] font-mono font-bold", pc.accent)}>
                                    {pc.icon} {pc.label}
                                  </span>
                                  {story.storyPoints && (
                                    <span className="text-[10px] font-mono text-foreground bg-secondary px-1.5 py-0.5 rounded-sm font-bold">
                                      {story.storyPoints}
                                    </span>
                                  )}
                                  {(story.acceptanceCriteria?.length || 0) > 0 && (
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                      <CheckCircle2 className="h-2.5 w-2.5" />
                                      {story.acceptanceCriteria.length}
                                    </span>
                                  )}
                                  {(story.dependencies?.length || 0) > 0 && (
                                    <span className="text-[10px] text-accent/70">
                                      🔗 {story.dependencies!.length}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {items.length === 0 && (
                    <div
                      className={cn(
                        "flex-1 min-h-[120px] rounded-md border border-dashed border-border/30 flex flex-col items-center justify-center gap-1 text-center px-4",
                        isOver && "border-accent/50 bg-accent/5"
                      )}
                    >
                      <ColIcon className="h-5 w-5 text-muted-foreground/20" />
                      <span className="text-[10px] text-muted-foreground/40 font-body">{col.emptyText}</span>
                    </div>
                  )}

                  {items.length > 0 && dragOverCol === col.id && dragOverIdx === items.length && (
                    <div className="h-0.5 bg-accent rounded-full animate-fade-in" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}
    </div>
  );
}
